import * as React from 'react';
import { Alert, Box, Button, Paper, Stack, TextField, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import ReCAPTCHA from 'react-google-recaptcha';
import { toast } from 'react-toastify';
import { emailRegex } from '../../../utils/regex';
import api from '../../../api/axiosInstance';
import { RECAPTCHA_SITE_KEY } from '../../../constants';
import { Loader } from '../../../components/loader';
import LayoutNoAuth from '../layout';

export default function ForgetPassword() {
  const navigate = useNavigate();
  const recaptchaRef = React.useRef<ReCAPTCHA>(null);

  const [email, setEmail] = React.useState('');
  const [emailError, setEmailError] = React.useState<string | null>(null);
  const [infoOpen, setInfoOpen] = React.useState(false);
  const [recaptchaToken, setRecaptchaToken] = React.useState<string | null>(null);

  const validateEmail = (value: string) => {
    if (!value.trim()) return 'Введите email';
    if (!emailRegex.test(value.trim())) return 'Некорректный email';
    return null;
  };

  React.useEffect(() => {
    if (!infoOpen) return;
    const t = setTimeout(() => setInfoOpen(false), 5000);
    return () => clearTimeout(t);
  }, [infoOpen]);

  const { mutate, isPending } = useMutation({
    mutationFn: async () => {
      await api.post('/auth/forgot-password', {
        email: email.trim(),
        recaptchaToken,
      });
    },
    onSuccess: () => {
      setInfoOpen(true);
      toast.success(
        'Если указанный email зарегистрирован, мы отправили инструкцию по восстановлению.',
      );
      setRecaptchaToken(null);
      recaptchaRef.current?.reset();
    },
    onError: () => {
      setInfoOpen(true);
      toast.info(
        'Если указанный email зарегистрирован, мы отправили инструкцию по восстановлению.',
      );
      setRecaptchaToken(null);
      recaptchaRef.current?.reset();
    },
  });

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const err = validateEmail(email);
    setEmailError(err);
    if (err) return;

    if (!recaptchaToken) {
      toast.error('Подтвердите reCAPTCHA');
      return;
    }

    mutate();
  };

  const currentEmailError = validateEmail(email);
  const isFormValid = !currentEmailError && !!recaptchaToken;

  return (
    <LayoutNoAuth>
      <Paper
        elevation={0}
        sx={{
          width: '100%',
          p: { xs: 2.5, sm: 3 },
          border: '1px solid rgba(0,0,0,0.08)',
          borderRadius: 2,
          boxShadow: '0 18px 45px rgba(15, 23, 42, 0.08)',
        }}
      >
        <Stack spacing={2.25}>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.75 }}>
              Восстановление доступа
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Укажите email аккаунта. Если он зарегистрирован, мы отправим ссылку для смены
              пароля.
            </Typography>
          </Box>

          <Box component="form" onSubmit={onSubmit}>
            <TextField
              fullWidth
              type="email"
              label="Email"
              value={email}
              onChange={e => {
                setEmail(e.target.value);
                if (emailError) setEmailError(null);
                if (infoOpen) setInfoOpen(false);
              }}
              onBlur={() => setEmailError(validateEmail(email))}
              error={!!emailError}
              helperText={emailError || ' '}
              disabled={isPending}
              autoComplete="email"
              InputLabelProps={{ shrink: true }}
            />

            <Box sx={{ my: 1.5, overflowX: 'auto' }}>
              <ReCAPTCHA
                ref={recaptchaRef}
                sitekey={RECAPTCHA_SITE_KEY}
                onChange={token => setRecaptchaToken(token)}
                onExpired={() => setRecaptchaToken(null)}
                onErrored={() => setRecaptchaToken(null)}
              />
            </Box>

            {infoOpen && (
              <Alert severity="info" sx={{ mb: 2 }}>
                Если указанный email зарегистрирован, мы отправили инструкцию по восстановлению.
              </Alert>
            )}

            <Stack spacing={1.25}>
              <Button
                type="submit"
                variant="contained"
                fullWidth
                disabled={!isFormValid || isPending}
                size="large"
              >
                {isPending ? <Loader /> : 'Отправить ссылку'}
              </Button>

              <Button
                variant="text"
                fullWidth
                onClick={() => navigate('/login')}
                disabled={isPending}
              >
                Назад ко входу
              </Button>
            </Stack>
          </Box>
        </Stack>
      </Paper>
    </LayoutNoAuth>
  );
}
